"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.classifyTimingForShownEvent = classifyTimingForShownEvent;
exports.classifyDecisionsStatusFromError = classifyDecisionsStatusFromError;
exports.extractErrorDetails = extractErrorDetails;
exports.attachClassifyTimingToError = attachClassifyTimingToError;
exports.classifyTimingFromError = classifyTimingFromError;
exports.captureOrdinalFromPhotoKey = captureOrdinalFromPhotoKey;
exports.dataUrlByteLength = dataUrlByteLength;
exports.rememberClassifyTimingForPhoto = rememberClassifyTimingForPhoto;
exports.classifyTimingForPhoto = classifyTimingForPhoto;
exports.toClassifyTimingProps = toClassifyTimingProps;
/** Stream-first (P9a): chips can show before Decisions returns. Mark status pending only when a call is in flight. */
function classifyTimingForShownEvent(timing, options) {
    if (timing?.decisionsStatus != null)
        return timing;
    // A stored timing (skip / empty ranked / settled) means the call is not in flight.
    if (timing)
        return { ...timing };
    if (options?.decisionsExpected === false)
        return {};
    return { decisionsStatus: 'pending' };
}
function classifyDecisionsStatusFromError(error) {
    const name = error && typeof error === 'object' ? error.name : undefined;
    return name === 'AbortError' || name === 'TimeoutError' ? 'abort' : 'error';
}
const MAX_ERROR_MESSAGE_LENGTH = 200;
function truncateErrorMessage(message) {
    if (!message || typeof message !== 'string')
        return undefined;
    return message.length > MAX_ERROR_MESSAGE_LENGTH
        ? message.slice(0, MAX_ERROR_MESSAGE_LENGTH)
        : message;
}
/** Extract non-PII error name and truncated message from an error. */
function extractErrorDetails(error) {
    if (!error)
        return {};
    if (error instanceof Error) {
        return {
            errorName: error.name || 'Error',
            errorMessage: truncateErrorMessage(error.message),
        };
    }
    if (typeof error === 'object') {
        const obj = error;
        return {
            errorName: typeof obj.name === 'string' ? obj.name : undefined,
            errorMessage: truncateErrorMessage(typeof obj.message === 'string' ? obj.message : undefined),
        };
    }
    return { errorMessage: truncateErrorMessage(String(error)) };
}
function attachClassifyTimingToError(error, timing) {
    if (error && typeof error === 'object') {
        error.classifyTiming = timing;
    }
    return error;
}
function classifyTimingFromError(error) {
    if (!error || typeof error !== 'object')
        return undefined;
    const timing = error.classifyTiming;
    return timing && typeof timing === 'object' ? timing : undefined;
}
function captureOrdinalFromPhotoKey(photoKey) {
    if (photoKey == null || photoKey === '')
        return undefined;
    const ordinal = Number(photoKey);
    return Number.isInteger(ordinal) && ordinal >= 0 ? ordinal : undefined;
}
function dataUrlByteLength(dataUrl) {
    const comma = dataUrl.indexOf(',');
    const b64 = comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
    if (!b64)
        return 0;
    const padding = b64.endsWith('==') ? 2 : b64.endsWith('=') ? 1 : 0;
    return Math.max(0, Math.floor((b64.length * 3) / 4) - padding);
}
function roundMs(value) {
    if (typeof value !== 'number' || !Number.isFinite(value))
        return undefined;
    return Math.max(0, Math.round(value));
}
function withCaptureOrdinal(photoKey, timing) {
    const captureOrdinal = timing.captureOrdinal ?? captureOrdinalFromPhotoKey(photoKey);
    return captureOrdinal == null ? timing : { ...timing, captureOrdinal };
}
/** Store Decisions timing under the photo that produced it. Never a last-write-wins slot. */
function rememberClassifyTimingForPhoto(store, photoKey, timing) {
    if (!photoKey || !timing)
        return;
    store.set(photoKey, withCaptureOrdinal(photoKey, timing));
}
function classifyTimingForPhoto(store, photoKey) {
    if (!photoKey)
        return undefined;
    return store.get(photoKey);
}
function toClassifyTimingProps(timing, extras) {
    const props = {};
    const addToRequestMs = roundMs(timing?.addToRequestMs);
    const prepMs = roundMs(timing?.prepMs);
    const decisionsLatencyMs = roundMs(timing?.decisionsLatencyMs);
    const modalToFirstChipMs = roundMs(extras?.modalToFirstChipMs);
    const captureOrdinal = extras?.captureOrdinal ??
        (typeof timing?.captureOrdinal === 'number' && Number.isInteger(timing.captureOrdinal) && timing.captureOrdinal >= 0
            ? timing.captureOrdinal
            : undefined);
    if (addToRequestMs != null)
        props.add_to_request_ms = addToRequestMs;
    if (prepMs != null)
        props.prep_ms = prepMs;
    if (typeof timing?.fileSizeBytes === 'number' && Number.isFinite(timing.fileSizeBytes)) {
        props.file_size_bytes = Math.max(0, Math.round(timing.fileSizeBytes));
    }
    if (typeof timing?.decisionsStatus === 'number' && Number.isFinite(timing.decisionsStatus)) {
        props.decisions_status = timing.decisionsStatus;
    }
    else if (typeof timing?.decisionsStatus === 'string' && timing.decisionsStatus) {
        props.decisions_status = timing.decisionsStatus;
    }
    if (decisionsLatencyMs != null)
        props.decisions_latency_ms = decisionsLatencyMs;
    if (modalToFirstChipMs != null)
        props.modal_to_first_chip_ms = modalToFirstChipMs;
    if (extras?.firstChipSource)
        props.place_first_chip_source = extras.firstChipSource;
    if (extras?.phase)
        props.phase = extras.phase;
    if (captureOrdinal != null)
        props.capture_ordinal = captureOrdinal;
    if (timing?.errorName)
        props.error_name = timing.errorName;
    if (timing?.errorMessage)
        props.error_message = timing.errorMessage;
    if (typeof timing?.hasAuthHeader === 'boolean')
        props.has_auth_header = timing.hasAuthHeader;
    return props;
}
